import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import AddIcon from '@mui/icons-material/Add';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';

import { BreadcrumbHeader, DetailPanel, StatusBadge, EmptyState, ActionMenu, ConfirmModal, EditSerialModal } from '../../components/shared';
import { parts, stockLevels, serialItems, inventoryAlerts, quoteHistory } from '../../services/dataClient';
import '../../styles/design-tokens.css';import ContentCard from "../../components/shared/ContentCard";

const headerCellSx = {
  backgroundColor: 'var(--secondary-light)',
  fontWeight: 600,
  fontSize: '0.75rem',
  color: 'var(--grey-700)',
  whiteSpace: 'nowrap',
  borderBottom: '1px solid var(--grey-200)',
  py: '12px',
  px: 2
};

// ─── Location detail modal ─────────────────────────────────────────────────────
function LocationDetailModal({ data, reorderThreshold, onClose, onAdd, onEdit, onDelete }) {
  if (!data) return null;
  const { sl, locationSerials, availableQty } = data;
  const effectiveThreshold = sl.reorderThreshold ?? reorderThreshold;
  const isLow = availableQty < effectiveThreshold;

  return (
    <Dialog
      open={Boolean(data)}
      onClose={(_, reason) => {if (reason !== 'backdropClick') onClose();}}
      maxWidth="sm"
      fullWidth>
      
      <DialogTitle sx={{ fontWeight: 600,
        fontSize: '1rem',
        color: 'var(--grey-900)',
        pb: 1,
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: 'var(--grey-900)' }}>
            {sl.locationName}
          </Typography>
          <StatusBadge status={sl.locationType} />
        </Box>
        <IconButton size="small" onClick={onClose} sx={{ color: 'var(--grey-400)', '&:hover': { color: 'var(--grey-700)' }, mr: -1 }}>
          <HighlightOffIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: 2.5, pb: 3, pt: '12px !important' }}>
        {/* Stats */}
        <Box sx={{ display: 'flex', gap: 3, mb: 2 }}>
          {[
          { label: 'Available', value: availableQty, alert: isLow },
          { label: 'On Hand', value: sl.quantityOnHand },
          { label: 'Threshold', value: effectiveThreshold },
          { label: 'Updated', value: sl.lastUpdated }]
          .map(({ label, value, alert }) =>
          <Box key={label} sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ fontSize: '0.6875rem', color: 'var(--grey-500)', mb: '2px' }}>{label}</Typography>
              <Typography sx={{ fontSize: '1rem', fontWeight: 700, color: alert ? '#C62828' : 'var(--grey-900)', lineHeight: 1.2 }}>
                {value}
              </Typography>
            </Box>
          )}
        </Box>

        {isLow &&
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, backgroundColor: '#FFF3E0', border: '1px solid #FFB300', borderRadius: '6px', px: 1.5, py: 1, mb: 2 }}>
            <WarningAmberIcon sx={{ fontSize: 16, color: '#E65100', flexShrink: 0 }} />
            <Typography sx={{ fontSize: '0.8125rem', color: '#BF360C' }}>
              Stock is below the minimum threshold of {effectiveThreshold}.
            </Typography>
          </Box>
        }

        <Divider sx={{ mb: 2 }} />

        {/* Serial items */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
          <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--grey-600)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
            Serial Items
          </Typography>
          <Button
            size="small"
            startIcon={<AddIcon sx={{ fontSize: '13px !important' }} />}
            onClick={() => {onClose();onAdd(sl.locationName);}}
            sx={{ fontSize: '0.8125rem', textTransform: 'none', color: 'var(--secondary-main)', p: '2px 8px', minWidth: 0, visibility: locationSerials.length === 0 && sl.quantityOnHand > 0 ? 'hidden' : 'visible' }}>
            
            Add Serial
          </Button>
        </Box>

        {locationSerials.length === 0 ?
        <Box sx={{ backgroundColor: 'var(--grey-50)', border: '1px solid var(--grey-200)', borderRadius: '8px', px: 2.5, py: 2 }}>
            <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600, color: 'var(--grey-700)', mb: '4px' }}>
              Bulk Inventory
            </Typography>
            <Typography sx={{ fontSize: '0.8125rem', color: 'var(--grey-500)' }}>
              {sl.quantityOnHand > 0 ?
            `${sl.quantityOnHand} unit${sl.quantityOnHand !== 1 ? 's' : ''} on hand — not individually serialized at this location.` :
            'No stock at this location.'}
            </Typography>
          </Box> :

        <Box sx={{ border: '1px solid var(--grey-200)', borderRadius: '8px', overflow: 'hidden' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ ...headerCellSx, py: '8px' }}>Serial #</TableCell>
                  <TableCell sx={{ ...headerCellSx, py: '8px' }}>Status</TableCell>
                  <TableCell sx={{ ...headerCellSx, py: '8px' }}>Ticket #</TableCell>
                  <TableCell sx={{ ...headerCellSx, py: '8px', width: 40 }} />
                </TableRow>
              </TableHead>
              <TableBody>
                {locationSerials.map((s) =>
              <TableRow key={s.id} sx={{ height: 40, '&:last-child td': { borderBottom: 'none' } }}>
                    <TableCell sx={{ px: 2, py: 0, borderBottom: '1px solid var(--grey-200)', fontSize: '0.8125rem', fontFamily: 'monospace', color: 'var(--grey-800)' }}>
                      {s.serialNumber}
                    </TableCell>
                    <TableCell sx={{ px: 2, py: 0, borderBottom: '1px solid var(--grey-200)' }}>
                      <StatusBadge status={s.status} />
                    </TableCell>
                    <TableCell sx={{ px: 2, py: 0, borderBottom: '1px solid var(--grey-200)', fontSize: '0.8125rem', color: s.ticketNumber ? 'var(--grey-600)' : 'var(--grey-300)' }}>
                      {s.ticketNumber ?? '—'}
                    </TableCell>
                    <TableCell sx={{ px: 1, py: 0, borderBottom: '1px solid var(--grey-200)', textAlign: 'right' }}>
                      <ActionMenu actions={[
                  { label: 'Edit', onClick: () => {onClose();onEdit(s);} },
                  { label: 'Delete', onClick: () => {onClose();onDelete(s.id);} }]
                  } />
                    </TableCell>
                  </TableRow>
              )}
              </TableBody>
            </Table>
          </Box>
        }
      </DialogContent>
    </Dialog>);

}

// ─── Stock by Location table ───────────────────────────────────────────────────
function StockByLocationTable({ levels, serials, reorderThreshold, onRowClick }) {
  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={headerCellSx}>Location Name</TableCell>
            <TableCell sx={headerCellSx}>Type</TableCell>
            <TableCell sx={headerCellSx}>Available</TableCell>
            <TableCell sx={headerCellSx}>On Hand</TableCell>
            <TableCell sx={headerCellSx}>Threshold</TableCell>
            <TableCell sx={headerCellSx}>Last Updated</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {levels.length === 0 ?
          <TableRow>
              <TableCell colSpan={6} sx={{ border: 'none', py: 4 }}>
                <Typography sx={{ color: 'var(--grey-400)', fontSize: '0.875rem', textAlign: 'center' }}>
                  No stock levels found.
                </Typography>
              </TableCell>
            </TableRow> :

          levels.map((sl) => {
            const effectiveThreshold = sl.reorderThreshold ?? reorderThreshold;
            const locationSerials = serials.filter((s) => s.locationName === sl.locationName);
            const availableQty = locationSerials.length > 0 ?
            locationSerials.filter((s) => s.status === 'Available').length :
            sl.quantityOnHand;
            const isZero = availableQty === 0;
            const isLow = !isZero && availableQty < effectiveThreshold;
            const qtyColor = isZero ? '#B71C1C' : isLow ? '#E65100' : 'var(--grey-800)';

            return (
              <TableRow
                key={sl.id}
                hover
                onClick={() => onRowClick({ sl, locationSerials, availableQty })}
                sx={{ height: 48, cursor: 'pointer', '&:last-child td': { borderBottom: 'none' } }}>
                
                  <TableCell sx={{ px: 2, py: 0, border: 'none', fontSize: '0.875rem', color: '#009688', fontWeight: 500 }}>
                    {sl.locationName}
                  </TableCell>
                  <TableCell sx={{ px: 2, py: 0, border: 'none' }}>
                    <StatusBadge status={sl.locationType} />
                  </TableCell>
                  <TableCell sx={{ px: 2, py: 0, border: 'none' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <Typography sx={{ fontSize: '0.875rem', fontWeight: isZero || isLow ? 700 : 400, color: qtyColor }}>
                        {availableQty}
                      </Typography>
                      {(isZero || isLow) && <WarningAmberIcon sx={{ fontSize: 14, color: '#E65100' }} />}
                    </Box>
                  </TableCell>
                  <TableCell sx={{ px: 2, py: 0, border: 'none', fontSize: '0.875rem', color: 'var(--grey-800)' }}>
                    {sl.quantityOnHand}
                  </TableCell>
                  <TableCell sx={{ px: 2, py: 0, border: 'none', fontSize: '0.875rem', color: 'var(--grey-500)' }}>
                    {effectiveThreshold}
                  </TableCell>
                  <TableCell sx={{ px: 2, py: 0, border: 'none', fontSize: '0.875rem', color: 'var(--grey-800)' }}>
                    {sl.lastUpdated}
                  </TableCell>
                </TableRow>);

          })
          }
        </TableBody>
      </Table>
    </TableContainer>);

}

// ─── Active Alerts card ────────────────────────────────────────────────────────
function ActiveAlertsCard({ alerts }) {
  return (
    <ContentCard
      sx={{ p: 3
      }}>
      
      <Typography sx={{ fontWeight: 600, fontSize: '0.9375rem', color: 'var(--grey-900)', mb: 2 }}>
        Active Alerts
      </Typography>
      <Divider sx={{ borderColor: 'var(--grey-200)', mb: 2 }} />
      {alerts.length === 0 ?
      <Box sx={{ py: 2 }}>
          <EmptyState title="No active alerts" description="This part has no inventory alerts." />
        </Box> :

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {alerts.map((alert) =>
        <Box
          key={alert.id}
          sx={{ display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 2,
            p: 2,
            borderRadius: '6px',
            backgroundColor: alert.alertType === 'Out of Stock' ? '#FFF5F5' : '#FFF3E0',
            border: `1px solid ${alert.alertType === 'Out of Stock' ? '#FFCDD2' : '#FFE0B2'}`
          }}>
          
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                <WarningAmberIcon
              sx={{ fontSize: 18,
                mt: '1px',
                color: alert.alertType === 'Out of Stock' ? '#B71C1C' : '#BF360C'
              }} />
            
                <Box>
                  <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--grey-900)' }}>
                    {alert.alertNumber} — {alert.locationName}
                  </Typography>
                  <Typography sx={{ fontSize: '0.75rem', color: 'var(--grey-500)', mt: '2px' }}>
                    Triggered {alert.triggeredDate}
                  </Typography>
                </Box>
              </Box>
              <StatusBadge status={alert.alertType} />
            </Box>
        )}
        </Box>
      }
    </ContentCard>);

}

// ─── Price History card ────────────────────────────────────────────────────────
function PriceHistoryCard({ partNumber }) {
  const history = quoteHistory
  .filter((h) => h.partNumber === partNumber)
  .sort((a, b) => a.responseDate < b.responseDate ? 1 : -1);

  const headerCellSx = {
    backgroundColor: 'var(--secondary-light)',
    fontWeight: 600,
    fontSize: '0.75rem',
    color: 'var(--grey-700)',
    whiteSpace: 'nowrap',
    borderBottom: '1px solid var(--grey-200)',
    py: '12px',
    px: 2
  };

  return (
    <ContentCard sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography sx={{ fontWeight: 600, fontSize: '0.9375rem', color: 'var(--grey-900)' }}>
        Supplier Price History
      </Typography>
      <Box sx={{ border: '1px solid var(--grey-200)', borderRadius: '8px', overflow: 'hidden' }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={headerCellSx}>Date</TableCell>
                <TableCell sx={headerCellSx}>Ticket</TableCell>
                <TableCell sx={headerCellSx}>Supplier</TableCell>
                <TableCell sx={headerCellSx}>Unit Cost</TableCell>
                <TableCell sx={headerCellSx}>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {history.length === 0 ?
              <TableRow>
                  <TableCell colSpan={5} sx={{ border: 'none', py: 4 }}>
                    <Typography sx={{ color: 'var(--grey-400)', fontSize: '0.875rem', textAlign: 'center' }}>
                      No quote history for this part.
                    </Typography>
                  </TableCell>
                </TableRow> :

              history.map((h) =>
              <TableRow
                key={h.id}
                sx={{ height: 48, borderBottom: '1px solid var(--grey-200)', '&:last-child td': { borderBottom: 'none' } }}>
                
                    <TableCell sx={{ px: 2, py: 0, border: 'none' }}>
                      <Typography sx={{ fontSize: '0.875rem', color: 'var(--grey-800)' }}>{h.responseDate}</Typography>
                    </TableCell>
                    <TableCell sx={{ px: 2, py: 0, border: 'none' }}>
                      <Typography sx={{ fontSize: '0.875rem', color: 'var(--grey-600)' }}>{h.ticketNumber}</Typography>
                    </TableCell>
                    <TableCell sx={{ px: 2, py: 0, border: 'none' }}>
                      <Typography sx={{ fontSize: '0.875rem', color: 'var(--grey-800)' }}>{h.supplierName}</Typography>
                    </TableCell>
                    <TableCell sx={{ px: 2, py: 0, border: 'none' }}>
                      <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: '#009688' }}>${h.unitCost.toFixed(2)}</Typography>
                    </TableCell>
                    <TableCell sx={{ px: 2, py: 0, border: 'none' }}>
                      {h.status === 'Accepted' ?
                  <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600, color: '#2E7D32' }}>✓ Accepted</Typography> :

                  <Typography sx={{ fontSize: '0.8125rem', color: 'var(--grey-400)' }}>Declined</Typography>
                  }
                    </TableCell>
                  </TableRow>
              )
              }
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </ContentCard>);

}

// ─── Page ─────────────────────────────────────────────────────────────────────
export default function PartInventoryDetailPage() {
  const { partId } = useParams();
  const navigate = useNavigate();

  const part = parts.find((p) => p.id === partId);
  const alerts = part ? inventoryAlerts.filter((a) => a.partNumber === part.partNumber) : [];

  // Local stock levels
  const [localLevels, setLocalLevels] = useState(
    () => part ? stockLevels.filter((sl) => sl.partNumber === part.partNumber) : []
  );

  // Local serials
  const [localSerials, setLocalSerials] = useState(
    () => part ? serialItems.filter((si) => si.partNumber === part.partNumber) : []
  );

  // Refresh serials from module-level array
  const refreshSerials = () =>
  setLocalSerials([...serialItems.filter((si) => si.partNumber === part.partNumber)]);

  // Recompute qty on hand from serial count, then refresh local levels
  const refreshLevels = () => {
    stockLevels
    .filter((sl) => sl.partNumber === part.partNumber)
    .forEach((sl) => {
      sl.quantityOnHand = serialItems.filter(
        (si) => si.partNumber === part.partNumber &&
        si.locationName === sl.locationName &&
        si.status === 'Available'
      ).length;
    });
    part.totalQuantity = stockLevels
    .filter((sl) => sl.partNumber === part.partNumber)
    .reduce((sum, sl) => sum + sl.quantityOnHand, 0);
    setLocalLevels([...stockLevels.filter((sl) => sl.partNumber === part.partNumber)]);
  };

  // Location detail modal
  const [locationModal, setLocationModal] = useState(null);

  // Serial modal state
  const [serialModal, setSerialModal] = useState(null); // null | { mode: 'add'|'edit', serial?: obj }
  const [deleteTarget, setDeleteTarget] = useState(null); // serial id | null

  const handleSerialSave = (values) => {
    if (serialModal.mode === 'add') {
      serialItems.unshift({
        id: `f3a4b5c6-0013-4000-8013-${Date.now()}`,
        partNumber: part.partNumber,
        ...values
      });
    } else {
      const s = serialItems.find((si) => si.id === serialModal.serial.id);
      if (s) Object.assign(s, values);
    }
    refreshSerials();
    refreshLevels();
    setSerialModal(null);
  };

  const handleSerialDelete = () => {
    const idx = serialItems.findIndex((si) => si.id === deleteTarget);
    if (idx !== -1) serialItems.splice(idx, 1);
    refreshSerials();
    refreshLevels();
    setDeleteTarget(null);
  };

  if (!part) {
    return (
      <Box sx={{ p: 3 }}>
        <EmptyState
          title="Part not found"
          description={`No part with ID "${partId}" exists in the system.`} />
        
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Typography
            onClick={() => navigate('/inventory/search')}
            sx={{ fontSize: '0.875rem', color: '#009688', cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}>
            
            ← Back to Inventory
          </Typography>
        </Box>
      </Box>);

  }

  const totalStock = localLevels.reduce((s, sl) => s + sl.quantityOnHand, 0);
  const serialCount = localSerials.length;

  const partInfoFields = [
  { label: 'Part #', value: part.partNumber },
  { label: 'Description', value: part.description },
  { label: 'Category', value: part.category },
  { label: 'OEM', value: part.oem ?? '—' },
  { label: 'Reorder Threshold', value: part.reorderThreshold },
  { label: 'Total Qty (all locations)', value: totalStock }];

  return (
    <Box sx={{ pt: 0, px: 3, pb: 3, display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* Header */}
      <BreadcrumbHeader
        title={part.partNumber}
        subtitle={part.description}
        crumbs={[
        { label: 'Inventory', path: '/inventory/search' },
        { label: part.partNumber }]
        } />

      {/* Two-column layout */}
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3, alignItems: 'flex-start' }}>
        {/* Left column */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, flex: 1, minWidth: 0 }}>
          <DetailPanel title="Part Info" fields={partInfoFields} />

          {/* Stock by Location */}
          <ContentCard
            sx={{ p: 3,
              display: 'flex',
              flexDirection: 'column',
              gap: 2
            }}>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography sx={{ fontWeight: 600, fontSize: '0.9375rem', color: 'var(--grey-900)' }}>
                Stock by Location
              </Typography>
              {serialCount > 0 &&
              <Chip
                label={`${serialCount} serial${serialCount !== 1 ? 's' : ''}`}
                size="small"
                sx={{ backgroundColor: '#E8F5E9', color: '#2E7D32', fontSize: '0.6875rem', fontWeight: 600, height: 20 }} />

              }
            </Box>

            <Box sx={{ border: '1px solid var(--grey-200)', borderRadius: '8px', overflow: 'hidden' }}>
              <StockByLocationTable
                levels={localLevels}
                serials={localSerials}
                reorderThreshold={part.reorderThreshold}
                onRowClick={(data) => setLocationModal(data)} />
              
            </Box>
          </ContentCard>

          <PriceHistoryCard partNumber={part.partNumber} />
        </Box>

        {/* Right column */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, width: { xs: '100%', md: 360 }, flexShrink: 0 }}>
          <ActiveAlertsCard alerts={alerts} />
        </Box>
      </Box>

      {/* Location detail modal */}
      <LocationDetailModal
        data={locationModal}
        reorderThreshold={part.reorderThreshold}
        onClose={() => setLocationModal(null)}
        onAdd={(locationName) => setSerialModal({ mode: 'add', serial: { locationName } })}
        onEdit={(row) => setSerialModal({ mode: 'edit', serial: row })}
        onDelete={(id) => setDeleteTarget(id)} />

      {/* Edit / Add serial modal */}
      <EditSerialModal
        open={Boolean(serialModal)}
        mode={serialModal?.mode ?? 'add'}
        partNumber={part?.partNumber ?? ''}
        serial={serialModal?.serial ?? null}
        onClose={() => setSerialModal(null)}
        onSave={handleSerialSave} />

      {/* Confirm delete serial */}
      <ConfirmModal
        open={Boolean(deleteTarget)}
        title="Delete Serial Number"
        message="This serial record will be permanently removed."
        confirmLabel="Delete"
        onConfirm={handleSerialDelete}
        onCancel={() => setDeleteTarget(null)} />
      
    </Box>);

}